/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit, ViewChild } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { createDefaultGridModel } from '@gh/common-internal-shared';
import {
  createDialogRoute, DataSet, DateTimeTypes, DictionaryItem, DictionaryService, FormState, FormStateBuilder, globalDictionaries, } from '@gh/common-shared';
import { DirtyType, EntityVersion, ID } from '@gh/common-shared-data';
import { TranslationService } from '@gh/core-mls';
import {
  AddressAutocompleteComponent,
  AddressComponents,
  AutocompleteLoadMoreEvent,
  DialogInput,
  DialogService,
  foldAddressComponentsForXero,
  noAnswer,
  NotificationService,
  WaitingService,
  yesAnswer,
  yesNoConfirmation,
} from '@gh/core-ui';
import { gridCollectionComparator, GridDefinition, GridDefinitionFactory } from '@gh/core-ui-grid';
import { asString, currentDate, isNotNil, sortByStringField } from '@gh/core-util';
import { concat$, throwError$ } from '@gh/rx';
import { map$, catchError$, filter$, switchAll$, switchMap$ } from '@gh/rx/operators';
import { first, isNil } from 'lodash';
import { Observable } from 'rxjs';
import { LeadListItem, QuoteSalesFolder } from '../../';
import { QuoteDetailsBusinessService } from '../../resources/quote-details-business.service';
import {
  CloneQuote,
  cloneQuoteComparator,
  CloneSalesFolder,
  cloneSalesFolderComparator, CloneSite, cloneSiteComparator,
  getCloneQuoteId,
  getCloneSalesFolderId, getCloneSiteId,
  QuoteCloneRequest,
} from '../../resources/quote.model';
import { SalesFolderStatus, SiteAddressInfo } from '@gh/builder-shared-data';
import { SiteDetailsBusinessService } from '../../resources/site-details-business.service';

export enum QuoteCloneDialogEntryPoint {
  CloneQuote,
  CloneChanges,
  CreateQuote,
  CloneQuoteFromRfq,
}

export interface QuoteCloneDataDialogConfig {
  contactId: ID;
  customerName: string;
  salesFolderId: ID;
  targetQuoteId?: ID;
  quotationId?: ID;
  targetQuoteVersion?: EntityVersion;
  entryPoint: QuoteCloneDialogEntryPoint;
}

@Component({
  standalone: false,
  selector: 'gh-quote-clone-data-dialog',
  templateUrl: 'quote-clone-data-dialog.component.html',
  styleUrls: ['quote-clone-data-dialog.component.scss'],
})
export class QuoteCloneDataDialogComponent implements OnInit, DialogInput<QuoteCloneDataDialogConfig> {
  @ViewChild(AddressAutocompleteComponent) addressAutocomplete: AddressAutocompleteComponent | undefined;

  input: QuoteCloneDataDialogConfig;
  isCreateSalesFolderMode = false;
  isCreateSiteMode = false;
  priorities: DictionaryItem<any>[];
  isContactSelected: boolean = false;

  createSfForm: FormState<QuoteSalesFolder>;
  createSiteForm: FormState<SiteAddressInfo>;
  showManualAddressFields = false;

  customer?: LeadListItem;
  lead?: any;
  salesFolders = new DataSet<CloneSalesFolder>(getCloneSalesFolderId, cloneSalesFolderComparator);
  quotes = new DataSet<CloneQuote>(getCloneQuoteId, cloneQuoteComparator);
  sites = new DataSet<CloneSite>(getCloneSiteId, cloneSiteComparator);

  salesFolderLoading = false;
  quoteLoading = false;
  siteLoading = false;

  showOverdue: boolean;
  showInactive: boolean;
  showActive: boolean;
  showCompleted: boolean;

  salesFolderGridDefinition: GridDefinition;
  quoteGridDefinition: GridDefinition;
  siteGridDefinition: GridDefinition;

  private selectedSalesFolder?: CloneSalesFolder;
  private selectedQuote?: CloneQuote;
  private selectedSite?: CloneSite;

  constructor(private dialogRef: MatDialogRef<any>,
    private translationService: TranslationService,
    private gridDefinitionFactory: GridDefinitionFactory,
    private businessService: QuoteDetailsBusinessService,
    private siteService: SiteDetailsBusinessService,
    // private leadService: LeadDetailsBusinessService,
    private notificationService: NotificationService,
    private dialogService: DialogService,
    private formStateBuilder: FormStateBuilder,
    private dictionaryService: DictionaryService,
    private waitingService: WaitingService) {
  }

  get title(): string {
    return {
      [QuoteCloneDialogEntryPoint.CloneQuote]: 'lbl_rfq_clone_quote',
      [QuoteCloneDialogEntryPoint.CloneChanges]: 'lbl_rfq_clone_quote_changes',
      [QuoteCloneDialogEntryPoint.CreateQuote]: 'lbl_create_quote',
      [QuoteCloneDialogEntryPoint.CloneQuoteFromRfq]: 'lbl_rfq_clone_quote',
    }[this.input.entryPoint];
  }

  get isQuotationSelected(): boolean {
    return isNotNil(this.selectedQuote);
  }

  get isSiteSelected(): boolean {
    return isNotNil(this.selectedSite);
  }

  get isSalesFolderSelected(): boolean {
    return isNotNil(this.selectedSalesFolder);
  }

  get isCloneAvailable(): boolean {
    return this.isCloneFromRfqMode
      ? (this.isSiteSelected && this.isSalesFolderSelected)
      : this.isQuotationSelected;
  }

  get selectedSalesFolderId(): Maybe<ID> {
    const { selectedSalesFolder } = this;

    return selectedSalesFolder ? selectedSalesFolder.salesFolderId : void 0;
  }

  get selectedQuoteId(): Maybe<ID> {
    const { selectedQuote } = this;

    return selectedQuote ? selectedQuote.id : void 0;
  }

  get selectedSiteId(): Maybe<ID> {
    const { selectedSite } = this;

    return selectedSite ? selectedSite.id : void 0;
  }

  get isCreateMode(): boolean {
    return this.input.entryPoint === QuoteCloneDialogEntryPoint.CreateQuote;
  }

  get isCloneFromRfqMode(): boolean {
    return this.input.entryPoint === QuoteCloneDialogEntryPoint.CloneQuoteFromRfq;
  }

  ngOnInit(): void {
    this.dictionaryService.getDictionaryFromGlobal(globalDictionaries.priority)
      .subscribe((res) => {
        this.priorities = sortByStringField(res, 'localization').map((item) => ({ name: item.localization, id: parseInt(item.itemCode) }));
      });

    this.salesFolderGridDefinition = this.gridDefinitionFactory.buildManualWithModel({
      columns: [{
        field: 'name',
        headerName: this.translationService.get('lbl_name'),
        width: 300,
        comparator: gridCollectionComparator(cloneSalesFolderComparator),
        valueGetter: ({ data }: { data: CloneSalesFolder }) =>
          this.isCloneFromRfqMode || this.isCreateMode
            ? `${asString(data.name)}`
            : `${DateTimeTypes.ShortDate.format(data.created)} - ${asString(data.name)}`,
      }],
      model: createDefaultGridModel({
        getId: getCloneSalesFolderId,
      }),
      options: { rowSelection: { mode: 'singleRow', enableClickSelection: true, checkboxes: false } },
    });

    this.quoteGridDefinition = this.gridDefinitionFactory.buildManualWithModel({
      columns: [{
        field: 'name',
        headerName: this.translationService.get('lbl_name'),
        width: 300,
        sort: 'asc',
        comparator: gridCollectionComparator(cloneQuoteComparator),
        valueGetter: ({ data }: { data: CloneQuote }) =>
          `${data.code}: ${DateTimeTypes.ShortDate.format(data.date)}: ${asString(data.name)}`,
      }],
      model: createDefaultGridModel({
        getId: getCloneQuoteId,
      }),
      options: { rowSelection: { mode: 'singleRow', enableClickSelection: true, checkboxes: false } },
    });

    this.siteGridDefinition = this.gridDefinitionFactory.buildManualWithModel({
      columns: [{
        field: 'name',
        headerName: this.translationService.get('lbl_name'),
        width: 300,
        comparator: gridCollectionComparator(cloneSiteComparator),
        valueGetter: ({ data }: { data: CloneSite }) =>
          ` ${asString(data.name)}`,
      }],
      model: createDefaultGridModel({
        getId: getCloneSiteId,
      }),
      options: { rowSelection: { mode: 'singleRow', enableClickSelection: true, checkboxes: false } },
    });

    const { contactId, customerName } = this.input;
    const customer = contactId ? <any>{
      contactId,
      name: customerName,
    } : void 0;
    this.customer = customer;
    this.onContactSelected(customer);
  }

  loadContacts(event: AutocompleteLoadMoreEvent): void {
    event.load(this.businessService.findLeadsByName(event.query, {
      showCompleted: true,
      showInactive: true,
      showActive: true,
    }));
  }

  onContactSelected(contact?: LeadListItem): void {
    this.selectedSalesFolder = void 0;
    this.selectedQuote = void 0;
    this.selectedSite = void 0;

    this.salesFolders.clear();
    this.quotes.clear();
    this.sites.clear();

    if (isNil(contact)) {
      this.isContactSelected = false;
      return;
    }

    this.isContactSelected = true;

    this.salesFolderLoading = true;
    this.businessService.getLead(contact.contactId).subscribe(
      (response) => {
        this.lead = response;
      });

    if (this.isCreateMode || this.isCloneFromRfqMode) {
      this.businessService.findAllActiveSalesFoldersByCustomer(contact.contactId).subscribe(
        (salesFolders) => {
          this.updateSalesFolderList(salesFolders);
        },
        (errors) => {
          this.salesFolderLoading = false;
          this.notificationService.processAll(errors);
        },
      );
    } else {
      this.businessService.findAllCloneSalesFoldersByCustomer(contact.contactId).subscribe(
        (salesFolders) => {
          this.updateSalesFolderList(salesFolders);
        },
        (errors) => {
          this.salesFolderLoading = false;
          this.notificationService.processAll(errors);
        },
      );
    }
  }

  updateSalesFolderList(salesFolders: any): void {
    this.salesFolderLoading = false;
    this.salesFolders.set(salesFolders);
    this.selectedSalesFolder = first(this.salesFolders.data);

    if (this.selectedSalesFolderId) {
      this.onSalesFolderSelected(this.selectedSalesFolderId);
    }
  }

  onSalesFolderSelected(id?: ID): void {
    if (isNil(id)) {
      return;
    }

    this.selectedSalesFolder = this.salesFolders.byId(id);
    if (this.isCreateMode || this.isCloneFromRfqMode) {
      this.siteLoading = true;
      this.businessService.findAllCloneSitesBySalesFolder(id).subscribe(
        ({ sites }) => {
          this.siteLoading = false;
          this.sites.set(sites);
          this.selectedSite = first(this.sites.data);
        },
        (errors) => {
          this.siteLoading = false;
          this.notificationService.processAll(errors);
        },
      );
    } else {
      this.quoteLoading = true;
      this.businessService.findAllCloneQuotesByCustomer(id).subscribe(
        (quotes) => {
          this.quoteLoading = false;
          this.quotes.set(quotes);
          this.selectedQuote = first(this.quotes.data);
        },
        (errors) => {
          this.quoteLoading = false;
          this.notificationService.processAll(errors);
        },
      );
    }
  }

  onQuoteSelected(id: ID): void {
    this.selectedQuote = this.quotes.byId(id);
  }

  onSiteSelected(id: ID): void {
    this.selectedSite = this.sites.byId(id);
  }

  onClone(): void {
    const { selectedQuote, selectedSalesFolder, selectedSite } = this;

    if (this.isCloneFromRfqMode) {

      if (isNil(selectedSite) || isNil(selectedSalesFolder) || isNil(this.input.quotationId)) {
        return;
      }
      const cloned$ = this.cloneDataFromRfq(
        selectedSalesFolder.salesFolderId,
        this.input.quotationId,
        selectedSite.id,
        { skipWithdrawnWarningCheck: false });

      cloned$.pipe(
        this.waitingService.operator(),
        catchError$((errors) => {
          if (errors.has('msg_pb_withdrawn_warning')) {
            const answer$ = this.dialogService.confirmation(yesNoConfirmation('msg_pb_withdrawn_warning'));
            const yesAnswer$ = answer$.pipe(filter$(yesAnswer), switchMap$(() => this.cloneDataFromRfq(
              selectedSalesFolder.salesFolderId,
              this.input.quotationId || '',
              selectedSite.id,
              { skipWithdrawnWarningCheck: true })));
            const noAnswer$ = answer$.pipe(filter$(noAnswer),
              map$(() => throwError$('msg_pb_withdrawn_denied')),
              switchAll$());

            return concat$(yesAnswer$, noAnswer$);
          } else {
            return throwError$(errors);
          }
        })).subscribe(
          (quote) => this.dialogRef.close(quote),
          this.notificationService.processAll);

    } else {

      if (isNil(selectedQuote) || isNil(selectedSalesFolder)) {
        return;
      }

      const cloned$ = this.cloneData(
        selectedSalesFolder.salesFolderId,
        selectedQuote.id,
        { skipWithdrawnWarningCheck: false });

      cloned$.pipe(
        this.waitingService.operator(),
        catchError$((errors) => {
          if (errors.has('msg_pb_withdrawn_warning')) {
            const answer$ = this.dialogService.confirmation(yesNoConfirmation('msg_pb_withdrawn_warning'));
            const yesAnswer$ = answer$.pipe(filter$(yesAnswer), switchMap$(() => this.cloneData(
              selectedSalesFolder.salesFolderId,
              selectedQuote.id,
              { skipWithdrawnWarningCheck: true })));
            const noAnswer$ = answer$.pipe(filter$(noAnswer),
              map$(() => throwError$('msg_pb_withdrawn_denied')),
              switchAll$());

            return concat$(yesAnswer$, noAnswer$);
          } else {
            return throwError$(errors);
          }
        })).subscribe(
          (quote) => this.dialogRef.close(quote),
          this.notificationService.processAll);
    }
  }

  onCreate(): void {
    const { selectedSite, selectedSalesFolder } = this;

    if (isNil(selectedSite) || isNil(selectedSalesFolder)) {
      return;
    }
    this.dialogRef.close({
      customerId: this.customer?.contactId,
      salesFolderId: this.selectedSalesFolderId,
      siteId: this.selectedSiteId,
    });
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onCreateSiteForm(): void {
    this.isCreateSiteMode = true;
    this.createSiteForm = this.formStateBuilder.buildPlain({
      value: {
        name: '',
        lotNumber: '',
        suburb: '',
        streetNumber: '',
        street: '',
      },
      children: {
        name: { required: true },
        lotNumber: void 0,
        suburb: void 0,
        streetNumber: void 0,
        street: void 0,
      },
    });
    this.showManualAddressFields = false;
  }

  onCreateSfForm(): void {
    this.isCreateSalesFolderMode = true;
    this.createSfForm = this.formStateBuilder.buildPlain({
      value: {
        name: '',
        priority: null,
      },
      children: {
        name: { required: true },
        priority: { required: true },
      },
    });
  }

  onApplyCreateSf(): void {

    if (isNil(this.customer)) {
      return;
    }

    this.isCreateSalesFolderMode = false;
    this.businessService.updateLead({
      contactId: this.customer.contactId,
      typeOfCustomer: this.lead.typeOfCustomer,
      salespersonId: this.lead.salespersonId,
      dirtyType: DirtyType.Updated,
      version: this.lead.version,
      salesFolder: {
        ...this.createSfForm.submitValue,
        createdLocal: currentDate(),
        dirtyType: DirtyType.New,
        stage: 0, // Stage.New,
        status: SalesFolderStatus.Active,
      },
    }).pipe(
      this.waitingService.operator())
      .subscribe((response) => {
        this.salesFolderLoading = true;
        this.notificationService.success('msg_created_successfully');

        if (isNil(this.customer)) {
          return;
        }

        this.businessService.findAllActiveSalesFoldersByCustomer(this.customer.contactId).subscribe(
          (salesFolders) => {
            this.salesFolderLoading = false;
            this.salesFolders.set(salesFolders);
            this.selectedSalesFolder = first(this.salesFolders.data);

            if (this.selectedSalesFolderId) {
              this.onSalesFolderSelected(this.selectedSalesFolderId);
            }
          },
          (errors) => {
            this.salesFolderLoading = false;
            this.notificationService.processAll(errors);
          },
        );
      }, this.notificationService.processAll);
  }

  onApplyCreateSite(): void {
    this.siteService.create(this.selectedSalesFolderId || '', this.createSiteForm.submitValue).pipe(
      this.waitingService.operator())
      .subscribe(
        (response) => {
          this.siteLoading = true;
          this.businessService.findAllCloneSitesBySalesFolder(this.selectedSalesFolderId || '').subscribe(
            ({ sites }) => {
              this.siteLoading = false;
              this.sites.set(sites);
              this.selectedSite = first(this.sites.data);
              this.notificationService.success('msg_created_successfully');
            },
            (errors) => {
              this.siteLoading = false;
              this.notificationService.processAll(errors);
            },
          );
          this.isCreateSiteMode = false;
        },
        this.notificationService.processAll);
  }

  onCancelCreateSf(): void {
    this.isCreateSalesFolderMode = false;
    this.createSfForm.refresh();
  }

  onCancelCreateSite(): void {
    this.isCreateSiteMode = false;
    this.createSiteForm.refresh();

    const hasAddressData = !!(this.createSiteForm.group.get('streetNumber')?.value ||
      this.createSiteForm.group.get('street')?.value ||
      this.createSiteForm.group.get('suburb')?.value);
    this.showManualAddressFields = hasAddressData;
  }

  onAddressChange() {
    const streetNumber = this.createSiteForm.group.get('streetNumber')?.value || '';
    const street = this.createSiteForm.group.get('street')?.value || '';
    const city = this.createSiteForm.group.get('suburb')?.value || '';

    const addressParts: string[] = [];
    if (streetNumber) addressParts.push(streetNumber);
    if (street) addressParts.push(street);
    if (city) addressParts.push(city);

    const newAddress = addressParts.join(', ');

    if (this.addressAutocomplete) {
      this.addressAutocomplete.inputValue = newAddress;
    }
  }

  onAddressSelected(components: AddressComponents): void {
    if (!components) return;

    const c = foldAddressComponentsForXero(components);

    this.createSiteForm.group.patchValue({
      streetNumber: c.streetNo || '',
      street: c.street || '',
      suburb: c.city || '',
    });

    ['streetNumber', 'street', 'suburb'].forEach(field => {
      const control = this.createSiteForm.group.get(field);
      if (control) control.markAsDirty();
    });
  }

  private cloneDataFromRfq(sfId: ID, quoteId: ID, siteId: ID, request: QuoteCloneRequest): Observable<any> {
    return this.businessService.cloneQuoteFromRfq(sfId, quoteId, siteId, request);
  }

  private cloneData(sfId: ID, quoteId: ID, request: QuoteCloneRequest): Observable<any> {
    switch (this.input.entryPoint) {
      case QuoteCloneDialogEntryPoint.CloneQuote:
        return this.businessService.cloneQuote(this.input.salesFolderId, quoteId, request);
      case QuoteCloneDialogEntryPoint.CloneChanges:
        return this.businessService.cloneVariations(
          this.input.salesFolderId,
          this.input.targetQuoteId!,
          {
            sourceQuoteId: quoteId,
            targetQuoteVersion: this.input.targetQuoteVersion!,
          });
      default:
        throw new Error(`Unknown entry point: ${this.input.entryPoint}`);
    }
  }
}

export const quoteCloneDataDialogRoute = createDialogRoute({
  component: QuoteCloneDataDialogComponent,
  config: { autoFocus: false },
});
